import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import DocumentGrid from "../../features/documents/components/DocumentGrid";
import { motion } from "framer-motion";
import { FolderIcon, XMarkIcon, PlusIcon, FunnelIcon, MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import UploadDocumentModal from "../../features/documents/components/UploadDocumentModal";
import { useNotification } from "../../context/NotificationContext";
import MiniLoader from "../../components/MiniLoader";

export default function Marketing() {
 const { showToast } = useNotification();
 const [documents, setDocuments] = useState([]);
 const [filteredDocs, setFilteredDocs] = useState([]);
 const [loading, setLoading] = useState(true);
 const [isModalOpen, setIsModalOpen] = useState(false);
 const [newDocument, setNewDocument] = useState({ nom: "", lien: "", type: "pdf", fichier: null });
 const [searchTerm, setSearchTerm] = useState("");
 const [filterType, setFilterType] = useState("all");

 const navigate = useNavigate();

 useEffect(() => {
 fetchDocuments();
 }, []);

 useEffect(() => {
 filterDocuments();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [searchTerm, filterType, documents]);

 const fetchDocuments = async () => {
 try {
 const res = await api.get("/ressources");
 const marketingDocs = res.data.filter((doc) => doc.categorie?.toLowerCase() === "marketing");
 setDocuments(marketingDocs);
 setFilteredDocs(marketingDocs);
 } catch (err) {
 console.error("❌ Erreur lors du chargement des documents :", err);
 } finally {
 setLoading(false);
 }
 };

 const filterDocuments = () => {
 let filtered = documents.filter((doc) =>
 doc.nom.toLowerCase().includes(searchTerm.toLowerCase())
 );

 if (filterType !== "all") {
 filtered = filtered.filter((doc) => doc.type === filterType);
 }

 setFilteredDocs(filtered);
 };

 const handleAddDocument = async () => {
 if (!newDocument.nom || (!newDocument.lien && !newDocument.fichier)) {
 showToast("Veuillez sélectionner un fichier ou renseigner un lien.", "error");
 return;
 }
 
 try {
 let res;
 
 if (newDocument.fichier) {
 const formData = new FormData();
 formData.append("nom", newDocument.nom);
 formData.append("type", newDocument.type);
 formData.append("categorie", "marketing");
 formData.append("file", newDocument.fichier);
 
 res = await api.post("/ressources/upload", formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 res = await api.post("/ressources", {
 ...newDocument,
 categorie: "Marketing"
 });
 }
 
 setDocuments([...documents, res.data]);
 setIsModalOpen(false);
 setNewDocument({ nom: "", lien: "", type: "pdf", fichier: null });
 showToast("Document ajouté avec succès", "success");
 } catch (err) {
 console.error("❌ Erreur lors de l'ajout du document :", err);
 showToast("Erreur lors de l'ajout du document.", "error");
 }
 }; 

 return (
 <div className="flex items-center justify-center h-[80vh] px-6 lg:px-20 py-10">
 {/* Box blanche principale */}
 <div className="relative w-screen bg-surface rounded-3xl shadow-2xl overflow-hidden h-[75vh] mt-20">
 
 {/* En-tête avec icône et boutons */}
 <div className="flex items-center justify-between p-6 border-b bg-surface-muted">
 <div className="flex items-center space-x-4">
 <motion.div 
 className="p-3 bg-surface shadow-md rounded-full"
 initial={{ opacity: 0, scale: 0.8 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ duration: 0.6 }}
 >
 <FolderIcon className="w-10 h-10 text-secondary" />
 </motion.div>

 <h1 className="text-3xl sm:text-4xl font-bold text-primary">
 Documents Marketing
 </h1>
 </div>

 {/* Actions (Ajout + Retour) */}
 <div className="flex space-x-3">
 <button 
 onClick={() => setIsModalOpen(true)}
 className="flex items-center px-3 py-2 bg-primary text-white rounded-lg shadow-md hover:bg-primary transition"
 >
 <PlusIcon className="w-5 h-5 mr-2" />
 Ajouter
 </button>

 <button 
 onClick={() => navigate("/documents")}
 className="p-2 bg-surface-muted hover:bg-ink-300 rounded-full transition-all"
 >
 <XMarkIcon className="w-6 h-6 text-muted" />
 </button>
 </div>
 </div>

 {/* 🔍 Barre de recherche et filtrage */}
 <div className="flex justify-between items-center px-6 py-4 border-b">
 {/* Barre de recherche */}
 <div className="relative w-2/3">
 <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-subtle" />
 <input
 type="text"
 placeholder="Rechercher un document..."
 className="w-full pl-10 pr-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-info-500"
 onChange={(e) => setSearchTerm(e.target.value)}
 />
 </div>

 {/* Bouton de filtrage */}
 <button
 onClick={() => setFilterType(filterType === "all" ? "pdf" : filterType === "pdf" ? "image" : "all")}
 className="flex items-center space-x-2 px-4 py-2 bg-surface-muted hover:bg-surface-muted rounded-lg shadow-md"
 >
 <FunnelIcon className="w-5 h-5 text-muted" />
 <span className="text-muted">{filterType === "all" ? "Tous" : filterType.toUpperCase()}</span>
 </button>
 </div>

 {/* Affichage des documents */}
 <div className="p-6 overflow-y-auto h-[55vh]">
 {loading ? (
 <MiniLoader />
 ) : filteredDocs.length > 0 ? (
 <DocumentGrid categorie="marketing" />
 ) : (
 <p className="text-subtle text-center">Aucun document disponible.</p>
 )}
 </div>
 </div>

 {/* 📌 Modal d'ajout de document */}
 <UploadDocumentModal
 isOpen={isModalOpen}
 onClose={() => setIsModalOpen(false)}
 onAdd={handleAddDocument}
 documentData={newDocument}
 setDocumentData={setNewDocument}
 />
 </div>
 );
}
